import React, { useRef, useState } from 'react';
import {
  Divider,
  Dropdown,
  DropdownItem,
  DropdownList,
  MenuToggle,
  type MenuToggleElement,
} from '@patternfly/react-core';
import { EllipsisVIcon } from '@patternfly/react-icons';
import type { NewsletterSummary } from '../../../types/models/Newsletter';

interface NewsletterRowActionsProps {
  newsletter: NewsletterSummary;
  onModify: (newsletter: NewsletterSummary) => void;
  onSend: (newsletter: NewsletterSummary) => void;
  /** Opens the preview popover, anchored to this row's kebab toggle (passed as the element). */
  onPreview: (newsletter: NewsletterSummary, anchor: HTMLElement | null) => void;
  /** Clone this newsletter (any status) into a fresh draft, then open it for editing. */
  onDuplicate: (newsletter: NewsletterSummary) => void;
  onDelete: (newsletter: NewsletterSummary) => void;
}

/** Assign one DOM node to several refs (PatternFly's toggle ref + our own popover-anchor ref). */
const mergeRefs =
  (...refs: React.Ref<MenuToggleElement>[]) =>
  (value: MenuToggleElement | null) => {
    refs.forEach((ref) => {
      if (typeof ref === 'function') {
        ref(value);
      } else if (ref !== null && ref !== undefined) {
        (ref as React.MutableRefObject<MenuToggleElement | null>).current = value;
      }
    });
  };

/**
 * Per-row kebab for a newsletter. A custom `Dropdown` (rather than `ActionsColumn`) so we keep a ref
 * to the toggle button — the "Preview" action anchors a popover to it — and so item visibility
 * tracks the lifecycle:
 *   • Modify / Send — only a plain DRAFT is editable and queueable;
 *   • Preview / Duplicate — always (a proof can be sent for, and any status can be cloned into a draft);
 *   • Delete / Archive — hidden while `sending` (the worker owns the row); labelled "Archive" for a
 *     sent-but-not-archived newsletter (the backend archives it), else "Delete".
 */
function NewsletterRowActions({ newsletter, onModify, onSend, onPreview, onDuplicate, onDelete }: NewsletterRowActionsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const toggleRef = useRef<MenuToggleElement | null>(null);

  const isDraft = newsletter.status === 'draft';
  const isSending = newsletter.status === 'sending';
  const willArchive = newsletter.is_sent && !newsletter.is_archived;

  return (
    <Dropdown
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      onSelect={() => setIsOpen(false)}
      popperProps={{ position: 'right' }}
      toggle={(pfRef) => (
        <MenuToggle
          ref={mergeRefs(pfRef, toggleRef)}
          aria-label={`Actions for ${newsletter.subject}`}
          variant="plain"
          isExpanded={isOpen}
          onClick={() => setIsOpen((open) => !open)}
        >
          <EllipsisVIcon />
        </MenuToggle>
      )}
    >
      <DropdownList>
        {isDraft && (
          <DropdownItem key="modify" onClick={() => onModify(newsletter)}>
            Modify
          </DropdownItem>
        )}
        <DropdownItem key="preview" onClick={() => onPreview(newsletter, toggleRef.current)}>
          Preview…
        </DropdownItem>
        <DropdownItem key="duplicate" onClick={() => onDuplicate(newsletter)}>
          Duplicate
        </DropdownItem>
        {isDraft && (
          <DropdownItem key="send" onClick={() => onSend(newsletter)}>
            Send…
          </DropdownItem>
        )}
        {!isSending && [
          <Divider key="sep" component="li" />,
          <DropdownItem key="delete" isDanger={!willArchive} onClick={() => onDelete(newsletter)}>
            {willArchive ? 'Archive' : 'Delete'}
          </DropdownItem>,
        ]}
      </DropdownList>
    </Dropdown>
  );
}

export default NewsletterRowActions;
